import { useRef } from 'react'

import { AnimatePresence } from 'framer-motion'

import useVirtualization from 'modules/core/hooks/useVirtualization'
import type { Access } from 'modules/core/store/models/Access'

import GradientShadow from 'modules/payroll_bundle/components/GradientShadow'
import { ImportData } from 'modules/payroll_bundle/lib/PayrollResultContext'

import Item from './_Item'
import './index.styles.css'
// Multiplier to calculate the height of the shadow
const HEIGHT_MULTIPLIER = 1.1

interface VirtualListProps {
  resultItems: ImportData[]
  containerHeight: number
  itemHeight: number
  selected?: Set<number>
  checkable?: boolean
  onSelectItem?: (resultItem: ImportData) => void
  getUserFromImportData: (item: ImportData) => Access | null | undefined
  onAssignEmployee: (userId: number, index: number) => void
  onUnAssignEmployee: (userId: number, index: number) => void
  currency: string
}

const VirtualList = ({
  resultItems,
  itemHeight,
  containerHeight,
  selected,
  checkable,
  getUserFromImportData,
  onSelectItem,
  onAssignEmployee,
  onUnAssignEmployee,
  currency,
}: VirtualListProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const {
    visibleStartIndex,
    endIndex,
    topPlaceholderHeight,
    bottomPlaceholderHeight,
    scrollTopShadow,
    scrollBottomShadow,
  } = useVirtualization({
    items: resultItems,
    defaultItemHeight: itemHeight,
    containerRef,
    shadowHeight: itemHeight * HEIGHT_MULTIPLIER,
  })

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        overflowY: 'auto',
        overflowX: 'auto',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <AnimatePresence>
          {scrollTopShadow && <GradientShadow direction="bottom" height={containerHeight} />}
        </AnimatePresence>
        <AnimatePresence>
          {scrollBottomShadow && <GradientShadow direction="top" height={containerHeight} />}
        </AnimatePresence>
      </div>

      <div style={{ height: `${topPlaceholderHeight}px` }}></div>

      <table className={'_GroupBody_table'}>
        {resultItems.slice(visibleStartIndex, endIndex).map((importedEmployee, index) => {
          const checked = selected?.has(importedEmployee.id)

          return (
            <Item
              key={importedEmployee.id}
              index={index}
              checkable={checkable}
              importedEmployee={importedEmployee}
              getUserFromImportData={getUserFromImportData}
              onSelectItem={onSelectItem}
              onAssignEmployee={onAssignEmployee}
              onUnAssignEmployee={onUnAssignEmployee}
              isSelected={checked}
              currency={currency}
            />
          )
        })}
      </table>
      <div style={{ height: `${bottomPlaceholderHeight}px` }}></div>
    </div>
  )
}

export default VirtualList
